import { isApiError, unwrap } from "@sagot/api-client";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { queueEntriesOf, type QueueInfo } from "./queue-sort";
import { useLiveChatState } from "./use-presence";

/** The inbox re-reads the order this often while the Needs staff view is open (the live-order card shares the cache entry). */
const QUEUE_POLL_MS = 10_000;

/**
 * `GET /v1/routing/live-chat/queue` for the Needs staff view: the order the weighted queue serves the waiting chats in, with the time
 * each has waited. `null` outside the `live_chat_ops` rollout (the probe is asked first, so no flagged route is requested there) and
 * while the order has not been read yet.
 */
export function useInboxQueue(enabled: boolean): QueueInfo | null {
  const api = useApi();
  const ws = useWorkspaceId();
  const state = useLiveChatState(enabled);
  const on = enabled && state.data?.enabled === true;
  const query = useQuery({
    queryKey: ["ws", ws, "routing", "live-chat", "queue"] as const,
    queryFn: () => unwrap(api.GET("/v1/routing/live-chat/queue")),
    enabled: on,
    retry: false,
    staleTime: 3_000,
    refetchInterval: (current) => (isApiError(current.state.error) && current.state.error.status === 404 ? false : QUEUE_POLL_MS),
  });
  if (!on || !query.data) return null;
  return { entries: queueEntriesOf(query.data.items), fetchedAt: query.dataUpdatedAt };
}
